import React, { useEffect } from 'react';
import { useHistory, Link } from 'react-router-dom';
import { useForm, FormProvider } from 'react-hook-form';

// UTILS
import { useQuery } from '../../util/hooks';

// REDUX AND SLICES
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../../reducers';
import { login } from '../../reducers/auth-slice';
import { fetchCheckoutAttributes } from '../../reducers/checkout-slice';

// WEB COMPONENTS
import { Button, IconLogo } from '@payright/web-components';

// OTHER COMPONENTS
import { ControlledInputField } from '../../components/form-fields';
import SCSpinner from '../../components/spinner';

// STYLES AND STYLED COMPONENTS
import SCEcommerceLogin from './css';
import { AppThunkDispatch } from '../../util/store';

interface LoginProps {}

type FormData = {
  username: string;
  password: string;
  code?: string;
};

const Login = (props: LoginProps) => {
  const history = useHistory();
  const dispatch = useDispatch<AppThunkDispatch>();

  // URL QUERY PARAMS
  const query = useQuery();
  const queryParamCheckoutId = query.get('checkoutId');

  // GLOBAL STATE
  const globalAuthState = useSelector((state: RootState) => state.auth);
  const globalCheckoutState = useSelector((state: RootState) => state.checkout);

  // VARIABLES
  const { errorMessage, isLoggedIn, loading } = globalAuthState;
  const { hasErrors } = globalCheckoutState;
  const checkoutType = globalCheckoutState.checkout?.attributes?.type;
  const status = globalCheckoutState.checkout?.status;

  const [displayMfaForm, setDisplayMfaForm] = React.useState(false);
  const [requestId, setRequestId] = React.useState('');
  const [phoneNumberHint, setPhoneNumberHint] = React.useState('');

  // START LOGIN FORM
  const reactHookForm = useForm<FormData>({
    defaultValues: {
      username: '',
      password: '',
      code: '',
    },
    mode: 'onChange',
    shouldUnregister: false,
  });
  const { handleSubmit, formState, setValue } = reactHookForm;

  const formSubmitHandler = async (formData: FormData) => {
    const result = await dispatch(
      login(
        formData.username,
        formData.password,
        displayMfaForm ? requestId : undefined,
        displayMfaForm ? formData.code : undefined
      )
    );

    if (result && 'mfa_required' in result) {
      setDisplayMfaForm(true);
      setRequestId(result.requestId);
      setPhoneNumberHint(result.phoneNumberHint);
      setValue('code', '');
    }
  };
  // END LOGIN FORM

  // LOAD LATEST CHECKOUT FROM THE SERVER
  useEffect(() => {
    if (queryParamCheckoutId) {
      dispatch(fetchCheckoutAttributes(queryParamCheckoutId));
    }
  }, [queryParamCheckoutId, dispatch]);

  // Redirect to Plan Purpose if checkout status is pending (and no plan is created yet)
  useEffect(() => {
    if (isLoggedIn && status === 'pending') {
      history.push('/payment-plan/plan-purpose' + history.location.search);
    }
  }, [isLoggedIn, history, checkoutType, status]);

  // DEMO LOGIN
  if (process.env.REACT_APP_DEMO_LOGIN === 'true') {
    setValue('username', 'demo@payright.com.au');
    setValue('password', 'demo_password');
  }

  return (
    <SCEcommerceLogin>
      <div className="ecommerce-image" />
      <div className="login-wrapper">
        <div className="login-header">
          <div className="logo">
            <IconLogo width={180} height={74} />
          </div>
          <h4>Login</h4>
        </div>
        <FormProvider {...reactHookForm}>
          <form onSubmit={handleSubmit(formSubmitHandler)} className="loginForm">
            <div data-testid="error-message">
              {errorMessage && (
                <div className="validation-msg-box">
                  {displayMfaForm
                    ? 'Invalid verification code. Please try again'
                    : 'Incorrect email or password'}
                </div>
              )}
            </div>
            <div>
              {hasErrors && <div className="validation-msg-box">Checkout ID is invalid</div>}
            </div>
            {displayMfaForm ? (
              <>
                <p style={{ marginBottom: '2em' }}>
                  Please enter the verification code that was sent to your mobile at{' '}
                  {phoneNumberHint}.
                </p>
                <ControlledInputField placeholder="Verification code" name="code" type="text">
                  Verification code
                </ControlledInputField>

                <div className="back-confirm">
                  <Link
                    to="#"
                    onClick={(e) => {
                      setDisplayMfaForm(false);
                    }}
                  >
                    Back
                  </Link>

                  <Button
                    withShadow
                    disabled={formState.isValid === false || loading}
                    maxWidth="80%"
                    type="submit"
                    iconPosition="right"
                    icon={loading ? <SCSpinner /> : undefined}
                  >
                    Verify
                  </Button>
                </div>
              </>
            ) : (
              <>
                <ControlledInputField
                  placeholder="Email address"
                  name="username"
                  type="email"
                  rules={{ required: true }}
                >
                  Email Address
                </ControlledInputField>
                <ControlledInputField
                  placeholder="Password"
                  name="password"
                  type="password"
                  rules={{ required: true }}
                >
                  Password
                </ControlledInputField>
                <div className="forgot-password">
                  <Link to={`/forgot-password/${history.location.search}`}>
                    <span className="hide-mobile">I've forgotten my password</span>
                    <span className="mobile-only">Forgot password?</span>
                  </Link>
                </div>
                <Button
                  withShadow
                  disabled={formState.isValid === false || loading}
                  maxWidth="100%"
                  type="submit"
                  iconPosition="right"
                  icon={loading ? <SCSpinner /> : undefined}
                >
                  Login
                </Button>
              </>
            )}
          </form>
        </FormProvider>
      </div>
    </SCEcommerceLogin>
  );
};

export default Login;
